// "Other rooms" — send this TV room's Apple TV audio to chosen Sonos rooms.
//
// Contract: HANDOFF-ha-av.md § "TV audio to chosen Sonos rooms". The panel
// calls script.tv_to_sonos {source, rooms} (sets the group to exactly
// `rooms`, so the same call starts and edits a session) and
// script.tv_to_sonos_stop {source}. It reads sensor.tv_to_sonos_rooms:
// state = active source or 'idle'; attributes candidates,
// excluded_by_source, members, busy. The script stays the authority; the
// greying here only mirrors it.
//
// Chip state comes from the sensor's `members`, never from Sonos playing
// state: grouped Sonos read `idle` for the first ~20 s of a working stream.
//
// In-flight feedback per the panel vocabulary: the chip takes the lit fill
// immediately, a BusyDot spins, the copy says what is happening, and the
// intent window is 40 s (Rumpus runs rumpus_listen, the add retries for
// up to ~15 s). Settles when the sensor agrees, or on the script's error.

import * as React from 'react';
import { A_FONT, ALabel, fs, BusyDot } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { SpeakerRow } from '../music/primitives.jsx';
import { SONOS_SPEAKERS } from '../hooks/use-panel-rooms.jsx';

const { useState, useEffect, useMemo } = React;

export const TV_TO_SONOS_SENSOR = 'sensor.tv_to_sonos_rooms';
export const TV_TO_SONOS_SOURCES = ['pavilion', 'rumpus', 'office'];

const INTENT_MS = 40000;
const FLOOR_MS = 1200;
const ASLEEP = ['off', 'standby', 'unavailable', 'unknown'];

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

function nameOf(entity) {
  return SONOS_SPEAKERS.find((s) => s.entity === entity)?.name
    || entity.split('.').pop().replace(/_/g, ' ');
}

// Why a candidate is greyed, in words, from the same facts the sensor used.
function busyBadge(entity, haStates) {
  const e = haStates?.[entity];
  if (entity === 'media_player.rumpus') {
    const tv = haStates?.['media_player.rumpus_tv']?.state;
    const atv = haStates?.['media_player.apple_tv_rumpus']?.state;
    if ((tv && !ASLEEP.includes(tv)) || (atv && !ASLEEP.includes(atv))) return 'TV in use';
  }
  if (e?.state === 'playing') {
    const t = e.attributes?.media_title;
    return t ? `playing · ${t}` : 'playing';
  }
  if ((e?.attributes?.group_members || []).length > 1) return 'in a group';
  return 'in use';
}

// Reads the sensor into the shape the UI needs, for one source room.
export function readTvToSonos(hass, sourceId) {
  const s = hass?.states?.[TV_TO_SONOS_SENSOR];
  if (!s || !TV_TO_SONOS_SOURCES.includes(sourceId)) return null;
  const a = s.attributes || {};
  const excluded = (a.excluded_by_source || {})[sourceId] || [];
  const candidates = (a.candidates || []).filter((e) => !excluded.includes(e));
  const active = s.state && s.state !== 'idle' && s.state !== 'unknown' && s.state !== 'unavailable'
    ? s.state : null;
  return {
    active,                                   // source owning the session, or null
    mine: active === sourceId,
    otherSource: active && active !== sourceId ? active : null,
    members: a.members || [],
    busy: a.busy || [],
    candidates,
  };
}

// The chip that sits in the "Playing in" row.
export function SonosRoomsChip({ T, info, open, pending, onToggle }) {
  const lit = (info.mine && info.members.length > 0) || pending;
  const count = pending ? pending.rooms.length : info.members.length;
  return (
    <button onClick={onToggle}
      aria-expanded={open}
      style={{
        flex: '1 1 0', minWidth: 120, height: 48, padding: '0 14px',
        border: 'none',
        background: lit ? T.selected : T.cardSoft,
        color: lit ? T.selectedInk : T.ink,
        borderRadius: RADIUS.control, cursor: 'pointer',
        fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        whiteSpace: 'nowrap',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        transition: 'background .18s ease-out, color .18s ease-out',
      }}>
      {pending && <BusyDot T={T} size={14} color={T.selectedInk}/>}
      Other rooms
      {lit && count > 0 && (
        <span style={{ fontSize: fs(T, 13), opacity: .8 }}>· {count}</span>
      )}
    </button>
  );
}

// The picker that opens under the row.
export function SonosRoomsPicker({ T, hass, sourceId, info, atvState, pending, setPending, onClose }) {
  const [selected, setSelected] = useState(() => new Set(info.members));
  const [error, setError] = useState(null);

  // Re-seed from HA when the session changes underneath us (another panel,
  // the room's Off) and we are not mid-send.
  const membersKey = info.members.join('|');
  useEffect(() => {
    if (!pending) setSelected(new Set(info.members));
  }, [membersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Settle: HA agrees with what we asked for (after the 1.2 s floor), or
  // the 40 s window lapses.
  useEffect(() => {
    if (!pending) return undefined;
    const elapsed = Date.now() - pending.at;
    const agrees = pending.kind === 'stop'
      ? !info.active
      : info.mine && sameSet(info.members, pending.rooms);
    if (agrees) {
      const t = setTimeout(() => setPending(null), Math.max(0, FLOOR_MS - elapsed));
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setPending(null);
      setError('Still waiting on the house. Check the rooms, or try again.');
    }, Math.max(0, INTENT_MS - elapsed));
    return () => clearTimeout(t);
  }, [pending, info.active, info.mine, membersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const asleep = ASLEEP.includes(atvState || 'unknown');
  const rows = useMemo(() => info.candidates.map((entity) => {
    const inSession = info.mine && info.members.includes(entity);
    const busy = !inSession && info.busy.includes(entity);
    return { entity, name: nameOf(entity), busy, badge: busy ? busyBadge(entity, hass?.states) : (inSession ? 'listening' : '') };
  }), [info.candidates, info.busy, info.members, info.mine, hass?.states]);

  const toggle = (entity) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(entity)) next.delete(entity); else next.add(entity);
    return next;
  });

  const want = Array.from(selected).filter((e) => info.candidates.includes(e));
  const unchanged = info.mine && sameSet(want, info.members);

  const send = () => {
    setError(null);
    if (want.length === 0) { stop(); return; }
    setPending({ kind: 'send', rooms: want, at: Date.now() });
    hass.callService('script', 'tv_to_sonos', { source: sourceId, rooms: want }, undefined, false)
      .catch((err) => {
        setPending(null);
        setError(err?.message || 'The house refused that.');
      });
  };
  const stop = () => {
    setError(null);
    setPending({ kind: 'stop', rooms: [], at: Date.now() });
    hass.callService('script', 'tv_to_sonos_stop', { source: sourceId }, undefined, false)
      .catch((err) => {
        setPending(null);
        setError(err?.message || 'Could not stop.');
      });
  };

  // Blocked states: say why rather than offering a list that cannot work.
  let blocked = null;
  if (info.otherSource) blocked = `Already sending from the ${info.otherSource[0].toUpperCase()}${info.otherSource.slice(1)}. Stop it there first.`;
  else if (asleep && !info.mine) blocked = 'Start something playing on the Apple TV first.';

  const status = pending
    ? (pending.kind === 'stop' ? 'Stopping…' : `Sending to ${pending.rooms.length} room${pending.rooms.length === 1 ? '' : 's'}…`)
    : info.mine && info.members.length
      ? `Playing in ${info.members.map(nameOf).join(', ')}`
      : 'Choose rooms to hear this TV';

  const cta = want.length === 0
    ? (info.mine ? 'Stop sending' : 'Pick rooms')
    : info.mine ? 'Update rooms' : `Send to ${want.length} room${want.length === 1 ? '' : 's'}`;
  const ctaDisabled = !!pending || (!info.mine && want.length === 0) || unchanged;

  return (
    <div style={{ marginTop: 14, borderTop: `1px solid ${T.hair}`, paddingTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <ALabel T={T}>Other rooms</ALabel>
          <div style={{
            fontFamily: A_FONT.sans, fontWeight: 500, fontSize: fs(T, 17), color: T.ink,
            lineHeight: 1.25, marginTop: 4, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {pending && <BusyDot T={T} size={14}/>}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{blocked || status}</span>
          </div>
        </div>
        <button onClick={onClose} style={{
          height: 44, padding: '0 20px', borderRadius: RADIUS.control, flexShrink: 0,
          border: 'none', background: T.cardSoft, color: T.ink, cursor: 'pointer',
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        }}>Done</button>
      </div>

      {!blocked && (
        <>
          <div style={{ marginTop: 6 }}>
            {rows.map((r) => (
              <SpeakerRow key={r.entity} T={T}
                name={r.name}
                state={r.busy ? { kind: 'tv-busy', badge: r.badge } : { kind: 'idle', badge: r.badge }}
                selectable
                selected={selected.has(r.entity)}
                onClick={() => !pending && toggle(r.entity)}/>
            ))}
          </div>

          {error && (
            <div role="alert" style={{
              marginTop: 10, fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft,
            }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            {info.mine && want.length > 0 && (
              <button onClick={stop} disabled={!!pending} style={{
                flex: '0 0 auto', height: 52, padding: '0 22px', borderRadius: RADIUS.control,
                border: 'none', background: T.cardSoft, color: T.ink,
                cursor: pending ? 'not-allowed' : 'pointer',
                fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
              }}>Stop sending</button>
            )}
            <button onClick={send} disabled={ctaDisabled} style={{
              flex: '1 1 auto', height: 52, padding: '0 28px', borderRadius: RADIUS.control,
              border: 'none',
              background: ctaDisabled ? T.cardSoft : T.primary,
              color: ctaDisabled ? T.mute : T.primaryInk,
              cursor: ctaDisabled ? 'not-allowed' : 'pointer',
              fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
              boxShadow: ctaDisabled ? 'none' : T.cardShadow,
              transition: 'background .12s, color .12s',
            }}>{cta}</button>
          </div>
        </>
      )}
    </div>
  );
}
